import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { APPLICATION_MODE_NEW, CanadaProvinceList } from "../../../../app/constants";
import { combineAddress, fullName } from "../../../../app/utils";
import { Button, LineSeperator } from "../../../../components/RegularComponents";
import { saveBusinessLicenceApplicationData } from "../../../../redux/slice/businessLicenceApplicationSlice";
import { setPrimaryAgencyProperty } from "../../../../redux/slice/primaryAgencyApplicationSlice";
import { InformationDetailPanelSection } from "../InformationDetailPanelSection";

export const ComplianceOfficerTab = (props) => {
    const { appData, applicationType } = props;
    const [sectionToEdit,setSectionToEdit] = useState([]);
    const dispatch = useDispatch()

    const provinceOptionElements = useMemo(() => {
        return Object.keys(CanadaProvinceList).map((key) => {
          const element = CanadaProvinceList[key];
          return (
            <option key={key} value={element}>
              {element}
            </option>
          );
        });
      }, []);
      
    const editAddressForm = (sectionKey, businessAddress) => {
        const onChange = (property, value) => {
            
            let updatedAddress = {...sectionToEdit[sectionKey]}
            Object.defineProperty(updatedAddress, property, {value : value, writable: true, enumerable: true})

            let updatedSectionToEdit = {...sectionToEdit}
            Object.defineProperty(updatedSectionToEdit, sectionKey, {
                value : updatedAddress, 
                writable: true, 
                enumerable: true
            })
            setSectionToEdit(updatedSectionToEdit)
        }

        return (
            <fieldset className={"ns-fieldset"} id="business-address-edit-form">
                <div className="ns-fieldset row bottomMarginNormal">
                    <label
                        className="ns-label"
                        autoComplete="street-address"
                        htmlFor="businessAddressStreetEdit">
                            Street Address
                    </label>
                    <textarea
                        className="fw-25 ns-input"
                        type="text"
                        id="businessAddressStreetEdit"
                        rows={4} 
                        cols={60}
                        name="city"
                        onChange={e => onChange("address1", e.target.value)}>
                            {businessAddress["address1"]}
                    </textarea>
                </div>
                <div className="ns-fieldset row bottomMarginNormal">
                    <label for="businessAddressCityEdit">City</label>
                    <input
                        className="fw-25 ns-input"
                        type="text"
                        id="businessAddressCityEdit"
                        name="city"
                        value={businessAddress["cityName"]}
                        onChange={e => onChange("cityName", e.target.value)} />
                </div>
                <div className="ns-fieldset row bottomMarginNormal">
                    <label
                        id={"mra-province-select-label"}
                        className="ns-label"
                        htmlFor={"mra-province-select-input"}
                    >
                        Province
                    </label>
                    <div className="select fw-25 inlineDisplay">
                        <select
                        style={{ width: "25em" }}
                        id={"mra-province-select-input"}
                        name={"mra-province-select-input"}
                        value={businessAddress["stateName"] ?? ""}
                        onChange={e => onChange("stateName", e.target.value)}
                        >
                        {provinceOptionElements}
                        </select>
                    </div>
                </div>
                <div className="ns-fieldset row">
                    <label htmlFor="businessAddressPINEdit">Postal Code</label>
                    <input
                        type="text"
                        className="fw-25"
                        id="businessAddressPINEdit"
                        name="pin"
                        value={businessAddress["postalCode"]}
                        onChange={e => onChange("postalCode", e.target.value)} 
                    />
                </div>
            </fieldset>
        )
    }

    const inputEditForm = (sectionKey) => {
        const value = sectionToEdit[sectionKey]
        
        const onChange = (changedValue) => {
            setSectionToEdit(Object.defineProperty({...sectionToEdit}, 
                sectionKey, 
                {value : changedValue, writable: true, enumerable: true})
            )
        }

        return (
            <fieldset className={"ns-fieldset"} id="edit-form">
                <div className="ns-fieldset row bottomMarginNormal">
                    <input
                        className="fw-25 ns-input"
                        type="text"
                        id="edit-form"
                        name="edit-form"
                        value={value}
                        onChange={(e) => {
                            onChange(e.target.value)
                        }}
                    />
                </div>
            </fieldset>
        )
    }

    const handleClick = (section) => {
        let data

        switch(section) {
            case 'name' : 
                data = appData?.personData
                break
            case 'addressData' : 
                data = appData?.personData?.addressData
                break
            case 'phoneNumber' : 
                data = appData?.personData?.phoneNumber
                break
            case "alternatePhoneNumber" : 
                data = appData?.personData?.alternatePhoneNumber ?? "NA"
                break
            case "email" :
                data = appData?.personData?.email
                break
            default : 
                data = ""
                break
        }

        if(typeof data !== "undefined") {
            let newSectionEditObj = {...sectionToEdit}
            Object.defineProperty(newSectionEditObj,section,{value: data, enumerable: true, writable: true})
            setSectionToEdit(newSectionEditObj)
        }
    }

    const handleSave = (section) => {
        if(section === "addressData") {
            dispatch(setPrimaryAgencyProperty({
                key: "personData",
                value : {...appData.personData, addressData : sectionToEdit[section]}
            }))
        } else if(section === "email") {
            dispatch(setPrimaryAgencyProperty({
                key: "personData",
                value : {...appData.personData, email: sectionToEdit[section]}
            })) 
        } else if(section === "phoneNumber") {
            dispatch(setPrimaryAgencyProperty({
                key: "personData",
                value : {...appData.personData, phoneNumber: sectionToEdit[section]}
            })) 
        } else if(section === "alternatePhoneNumber") {
            dispatch(setPrimaryAgencyProperty({
                key: "personData",
                value : {...appData.personData, alternatePhoneNumber: sectionToEdit[section]}
            })) 
        }

        dispatch(saveBusinessLicenceApplicationData(applicationType, APPLICATION_MODE_NEW))
            .then(data => {
                // save successful
                let newSectionEditObj = {...sectionToEdit}
                delete newSectionEditObj[section]
                setSectionToEdit(newSectionEditObj)
            })
            .catch(err => {
                // save error
            })
    }

    const roleWithCompany = useCallback(() => {
        const peopleList = appData?.companyRjscDetails?.businessData?.peopleList
        const thePerson = peopleList.filter(p => (p.firstName === appData?.personData?.firstName && p.lastName === appData?.personData?.lastName))
        if(thePerson.length > 0) {
            return thePerson.map(a => a.position).join(" / ")
        }
        
        return "NA"
    }, [appData?.companyRjscDetails, appData?.personData])

    return (
        <div style={{marginTop: "1em"}}>
            <InformationDetailPanelSection 
                header={"Name"}
                value={fullName(appData?.personData)} />
            <LineSeperator />

            <InformationDetailPanelSection 
                header={"Role with company"}
                value={roleWithCompany()} />
            <LineSeperator />

            <InformationDetailPanelSection 
                header={"Home address"} 
                value={sectionToEdit["addressData"] ? 
                    editAddressForm("addressData",sectionToEdit["addressData"]) :
                    combineAddress(appData?.personData?.addressData)
                } >
                {sectionToEdit["addressData"] ?
                    <Button 
                        buttonType="regular_success" 
                        text={"Save"} 
                        onClick={() => handleSave("addressData")} 
                    /> : 
                     <Button 
                        buttonType="regular_primary" 
                        text={"Edit"} 
                        onClick={() => handleClick("addressData")} 
                    />}
            </InformationDetailPanelSection>
            <LineSeperator />

            <InformationDetailPanelSection 
                header={"Phone number"} 
                value={("phoneNumber" in sectionToEdit) ? inputEditForm("phoneNumber",sectionToEdit["phoneNumber"]) : appData?.personData?.phoneNumber} >
                {("phoneNumber" in sectionToEdit) ?
                    <Button 
                        buttonType="regular_success" 
                        text={"Save"} 
                        onClick={() => handleSave("phoneNumber")} 
                    /> : 
                     <Button 
                        buttonType="regular_primary" 
                        text={"Edit"} 
                        onClick={() => handleClick("phoneNumber")} 
                    />}
            </InformationDetailPanelSection>
            <LineSeperator />

            <InformationDetailPanelSection 
                header={"Alternate phone number"} 
                value={("alternatePhoneNumber" in sectionToEdit) ? inputEditForm("alternatePhoneNumber",sectionToEdit["alternatePhoneNumber"]) : appData?.personData?.alternatePhoneNumber} >
                {("alternatePhoneNumber" in sectionToEdit) ?
                    <Button 
                        buttonType="regular_success" 
                        text={"Save"} 
                        onClick={() => handleSave("alternatePhoneNumber")} 
                    /> : 
                     <Button 
                        buttonType="regular_primary" 
                        text={"Edit"} 
                        onClick={() => handleClick("alternatePhoneNumber")} 
                    />}
            </InformationDetailPanelSection>
            <LineSeperator />

            <InformationDetailPanelSection 
                header={"Email address"} 
                value={("email" in sectionToEdit) ? inputEditForm("email",sectionToEdit["email"]) : appData?.personData?.email} >
                {("email" in sectionToEdit) ?
                    <Button 
                        buttonType="regular_success" 
                        text={"Save"} 
                        onClick={() => handleSave("email")} 
                    /> : 
                     <Button 
                        buttonType="regular_primary" 
                        text={"Edit"} 
                        onClick={() => handleClick("email")} 
                    />}
            </InformationDetailPanelSection>
            <LineSeperator />
        </div>
    )
}